// filepath: apps/web/src/features/lists/gantt/hooks/useGanttMove.ts
//
// FR-UI-VIEW-3 AC-3 / AC-4 — useMutation hook for the Gantt
// drag-to-edit bar move action.
//
// SHAPE
//   PUT /api/entity-records/{entityType}/{id} with body
//     {
//       fields: {
//         [startField]: newStartIsoString,
//         [endField]:   newEndIsoString
//       },
//       row_version: N
//     }
//
// The server enforces INV-7 (row_version OCC). Three response paths:
//
//   1. 200 OK — record updated; the hook invalidates the Gantt cache
//      so the next render picks up the new bar placement.
//
//   2. 409 version_conflict — server rejected because the local
//      row_version is stale. The 409 envelope carries
//      `current_row_version` so the SPA can wire up the FR-UI-CORE-6
//      conflict flow. The hook returns the conflict in its `error`
//      channel — the parent (GanttView) routes it to the conflict
//      resolver and re-fetches on resolution.
//
//   3. 422 unprocessable_entity — server rejected because the new
//      start / end values would invert the existing depends_on
//      graph (a constraint check at the record layer). The hook
//      throws an `HttpValidationError` carrying a single-row
//      FieldErrorMap and the parent routes to the ValidationError
//      primitive.
//
// INVARIANTS
//   - INV-7: row_version is carried in the BODY (not If-Match) per
//     internal/entity/record_handlers.go UpdateRecordRequest.
//   - INV-2: the response body's record is server-stripped per
//     CanRead. We do NOT merge the local pending fields into the
//     returned record. The cache invalidation refetches the full
//     records set on the next render.
//   - Three other 4xx paths are mapped to typed errors so the
//     caller can decide whether to surface a fetch-failure,
//     permission-denied, or rate-limit surface.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import {
  ApiError,
  HttpConflictError,
  HttpValidationError,
  type FieldErrorMap,
} from "@/definitions/types";
import { apiFetch } from "@/api";
import { ganttKeys } from "./useGanttRecords";

/**
 * The mutation input — describes ONE drag-to-edit bar move. The
 * caller provides both the new start and the new end datetime values
 * (ISO-8601 strings) because a drag may move either end of the bar;
 * the mutation is atomic on both fields.
 */
export interface GanttMoveInput {
  /** UUID of the record being moved. */
  recordId: string;
  /**
   * The gantt sub-config's start_field name. Carried in the mutation
   * input (not read from the hook options) so a parent that
   * manipulates multiple start_fields in one render frame can
   * re-use the hook without re-keying.
   */
  startField: string;
  /** The gantt sub-config's end_field name. */
  endField: string;
  /** New ISO-8601 start datetime. */
  newStartIso: string;
  /** New ISO-8601 end datetime. */
  newEndIso: string;
  /**
   * Row version captured at drag-start (INV-7 OCC). The server
   * compares against the record's current row_version; on mismatch
   * it returns 409 + the current_row_version so the SPA can
   * reconcile.
   */
  rowVersion: number;
}

export interface GanttMoveError {
  /**
   * One of:
   *   - HttpConflictError → server returned 409. The hook's
   *     `onError` should surface this to the FR-UI-CORE-6 conflict
   *     flow (parent opens `useConflictResolver.show()`).
   *   - HttpValidationError → server returned 422.
   *   - ApiError (any other non-2xx).
   *
   * The caller inspects the type via `instanceof` to route to the
   * right surface (conflict modal / ValidationError / generic
   * FetchError respectively).
   */
  error: ApiError;
  /**
   * The original mutation input. Surfaced so the parent can roll
   * back optimistic state without re-deriving the move.
   */
  input: GanttMoveInput;
}

export interface UseGanttMoveOptions {
  /** Entity type — used to build the PUT URL and the cache key. */
  entityType: string;
  /** Definition version driving the cache key. */
  viewConfigVersion: number;
  /**
   * The gantt sub-config's start_field name — the 6th cache-key
   * segment (see ganttKeys.window). When the start_field changes
   * (definition republish) the cache key changes automatically
   * and the existing mutation invalidation pattern still works.
   */
  startField: string;
  /** API base URL override. Defaults to `/api`. */
  apiBase?: string;
}

export type UseGanttMoveResult = ReturnType<typeof useGanttMove>;

/**
 * useGanttMove — TanStack Query mutation for the drag-to-edit
 * bar move.
 *
 * Usage (from GanttView):
 *
 *   const move = useGanttMove({
 *     entityType, viewConfigVersion, startField,
 *   });
 *   move.mutate({
 *     recordId, startField, endField,
 *     newStartIso, newEndIso, rowVersion,
 *   }, {
 *     onError: (err, input) => {
 *       rollBackLocalState(input);
 *       if (err instanceof HttpConflictError) {
 *         conflictResolver.show(err.currentRowVersion);
 *       } else {
 *         surfaceError(err);
 *       }
 *     },
 *     onSuccess: () => { /* refetch already invalidated cache *\/ },
 *   });
 *
 * The hook DOES NOT call any surface-rendering code directly — the
 * parent owns the "route 409 → conflict modal; route 422 →
 * ValidationError; route other → FetchError" decision (per the
 * FR-UI-CORE-10 status envelope contract).
 */
export function useGanttMove(opts: UseGanttMoveOptions) {
  const { entityType, viewConfigVersion, startField, apiBase = "/api" } = opts;
  const queryClient = useQueryClient();
  const { tenantSlug } = useTenant();

  const mutation = useMutation<
    void,
    ApiError,
    GanttMoveInput,
    { previousKey: readonly unknown[] }
  >({
    // TanStack Query mutationKey (purely for devtools — invalidation
    // uses queryClient.invalidateQueries below).
    mutationFn: async (input: GanttMoveInput) => {
      const url = `${apiBase}/entity-records/${encodeURIComponent(
        entityType,
      )}/${encodeURIComponent(input.recordId)}`;

      const body = JSON.stringify({
        // INV-2 — we only send the start_field + end_field pair.
        // Other fields are untouched; we use the server-stripped
        // record as the source of truth. A subsequent GET picks up
        // the rest. The label_field is also untouched (it's a
        // string field — drag-to-edit manipulates the dates, not
        // the label).
        fields: {
          [input.startField]: input.newStartIso,
          [input.endField]: input.newEndIso,
        },
        row_version: input.rowVersion,
      });

      let response: Response;
      try {
        // ISS-377 round 3 R2-B2.5: migrated to apiFetch. The
        // INV-7 row_version is forwarded via the body; the
        // optimistic-concurrency 409 + 422 paths are preserved
        // verbatim.
        response = await apiFetch(url, {
          method: "PUT",
          init: {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body,
          },
        });
      } catch (networkErr) {
        throw new ApiError(
          networkErr instanceof Error ? networkErr.message : "network error",
          0,
          "network_error",
        );
      }

      if (response.ok) {
        return;
      }

      // All error paths share the same body parse.
      const errBody = await safeReadJson(response);
      const code =
        (errBody as { code?: string } | null)?.code ??
        `http_${response.status}`;
      const message =
        (errBody as { error?: string } | null)?.error ?? response.statusText;

      switch (response.status) {
        case 409: {
          // FR-ENT-8 / FR-UI-CORE-6 — the version-conflict envelope.
          // Mirrors internal/entity/record_handlers.go serveUpdate:
          //   { error, code: "version_conflict", current_row_version: N }
          // No `current_record` key — only the row_version is refreshed.
          const currentRowVersion = readCurrentRowVersion(errBody);
          throw new HttpConflictError(
            message,
            409,
            code,
            currentRowVersion,
            null,
          );
        }
        case 422: {
          // FR-UI-VIEW-3 AC-4 — the server returned a validation
          // envelope. When the wire is the per-field shape
          //   { errors: { field: { code, message } } }
          // the hook parses it into FieldErrorMap and wraps in an
          // HttpValidationError. Legacy shapes (a flat string) are
          // surfaced under the synthetic "__non_field_error__" key
          // so the ValidationError primitive renders them as one row.
          const errors = parseValidationErrors(errBody);
          throw new HttpValidationError(message, 422, code, errors);
        }
        case 403:
          throw new ApiError(message, 403, code);
        case 404:
          // Distinct from 403 for handlers that need to surface a
          // not-found modal (e.g. record deleted concurrently).
          throw new ApiError(message, 404, code);
        case 429: {
          const retryAfter = parseRetryAfter(
            response.headers.get("Retry-After"),
          );
          throw new ApiError(message, 429, code, retryAfter);
        }
        default:
          throw new ApiError(message, response.status, code);
      }
    },

    // AC-3: do NOT apply optimistic state — the renderer returns the
    // bar to its original position on a 4xx/5xx (the parent owns the
    // rollback logic via onError). For now we invalidate the gantt
    // cache so any layout derived from `staleTime` flushes on the
    // next render; the manual update via PutMutation happens below.
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ganttKeys.window(
          tenantSlug,
          entityType,
          viewConfigVersion,
          startField,
        ),
      });
    },
    onError: () => {
      // No cache invalidation on error — the roll-back is the
      // parent's responsibility (see GanttView onError wiring).
    },
  });

  return mutation;
}

// ----------------- tiny helpers (local — not exported) ---------------

async function safeReadJson(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

function parseRetryAfter(raw: string | null): number | null {
  if (!raw) return null;
  const seconds = Number.parseInt(raw, 10);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds;
  return null;
}

function readCurrentRowVersion(body: unknown): number | null {
  const raw = (body as { current_row_version?: unknown } | null)
    ?.current_row_version;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  return null;
}

/**
 * Parse a 422 wire envelope into a FieldErrorMap. The expected
 * shape is
 *   { errors: { fieldId: { code: string, message: string | {locale: value} } } }
 *
 * The FR-UI-VIEW-3 AC-4 single-line server envelope
 * ("invalid_gantt_field" — single sentinel string, no per-field
 * detail) is normalised to a one-row FieldErrorMap under the
 * synthetic "__non_field_error__" key so the ValidationError
 * primitive can render it as a single card.
 */
function parseValidationErrors(body: unknown): FieldErrorMap {
  const errorsBlock = (body as { errors?: unknown } | null)?.errors;
  if (errorsBlock && typeof errorsBlock === "object") {
    const out: FieldErrorMap = {};
    for (const [fieldId, entry] of Object.entries(
      errorsBlock as Record<string, unknown>,
    )) {
      if (entry && typeof entry === "object") {
        const e = entry as { code?: unknown; message?: unknown };
        if (typeof e.code === "string" && typeof e.message === "string") {
          out[fieldId] = { code: e.code, message: e.message };
          continue;
        }
      }
      // Defensive — malformed entry, surface as a plain text row.
      if (typeof entry === "string") {
        out[fieldId] = { code: "invalid", message: entry };
      }
    }
    if (Object.keys(out).length > 0) return out;
  }
  // Legacy flat-string or empty envelope — synthesise a non-field row.
  const fallbackMessage =
    (body as { error?: unknown } | null)?.error ??
    "Server returned 422 without a per-field envelope";
  if (typeof fallbackMessage === "string" && fallbackMessage !== "") {
    return {
      __non_field_error__: {
        code: "validation_failed",
        message: fallbackMessage,
      },
    };
  }
  return {};
}
